import BCMSDispatchHandler from "./src/BCMSDispatchHandler";

export default class BCMSDispatcher {
    _handlers = []; // Variable to store all added handlers

    // CONSTRUCTOR :: ARRAY -> BCMSDispatcher
    // Sets the property of "handlers"
    constructor(handlers){
        this.handlers = handlers == undefined ? [] : handlers;
    }

    /*
        handlers :: * -> VOID

        Setter for handlers. The "val" parameter is checked to make sure
        it is an instanceof ARRAY and that all elements within the array
        are of type BCMSDispatchHandler. If both conditions aren't met
        throw an error
    */
    set handlers(val) {
        if(val instanceof Array){
            if(val.every(item => item instanceof BCMSDispatchHandler)){
                this._handlers = val;
            }
            else{
                throw new Error("Handlers must be an array of BCMSDispatchHandlers");
            }
        }
        else{
            throw new Error("Handlers must be an ARRAY");
        }
    }

    // handlers :: * -> ARRAY
    // Getter for handlers
    get handlers() {
        return this._handlers;
    }

    /*
        add :: STRING, (state, action) -> state, BOOLEAN -> SELF

        Create a new BCMSDispatchHandler passing the id and fn parameters.
        Then check if the handler id exists, if the handler id already exists and override is set to false
        throw an error. Otherwise add the new handler to the _handler property.
    */
    add(id, fn, override){
        const handler = BCMSDispatchHandler.init(id, fn);

        if(!this.has(handler.id) || override){
            this._handlers.push(handler);
        }
        else{
            throw new Error("Event has already been bound");
        }

        return this;
    }

    // has :: STRING -> BOOLEAN
    // Checks if the passed id already exists within the handlers
    has(id){
        return this.ids.includes(id);
    }

    /*
        compose :: BCMSDispatcher, BOOLEAN -> SELF

        Reduce over the handlers of the passed dispatcher, check each handler id against the existing
        handlers. If the id conflicts but the override property is set to true then override the existing.
        Otherwise if the id does not exist add it to the handlers array.
    */
    compose(dispatcher, override){
        this.handlers = dispatcher.handlers.reduce((result, handler) => {
            if(this.has(handler.id) && override){
                const existing = result.filter(item => item.id == handler.id, [])[0];

                result.splice(result.indexOf(existing), 1);
                result.push(handler);
            }
            else if(!this.has(handler.id)){
                result.push(handler);
            }

            return result;
        }, this.handlers);

        return this;
    }



    /// ids :: * -> ARRAY
    // Getter that returns an array of ids for all handlers
    get ids() {
        return this.handlers.reduce((result, handler) => result.concat(handler.id), []);
    }

    // toMap :: * -> Map
    // Getter that returns a map of handler ids and fns
    get toMap() {
        return new Map(
          this.handlers.map(handler => [ handler.id, handler.fn ])
        );
    }

    // generate :: * => NULL
    // Return a function that takes in two parameters, Check to see if the passed type
    // exists, if it exists then return the bound function passing the state and action parameters.
    // Otherwise throw a new error.
    generate() {
        const handlerMap = this.toMap;

        return (state, action) => {
            if(this.has(action.type)){
                return handlerMap.get(action.type)(state, action);
            }
            throw new Error("Dispatch failed due to event not being bound");
        };
    }
    

    // composeAll :: [BCMSDispatcher], OBJECT, BOOLEAN => BCMSDispatcher
    // Take an array of dispatchers and compose all dispatchers together in a new dispatcher
    static composeAll(dispatchers, inital, override){
        return dispatchers.reduce((result, dispatcher) => {
            result.compose(dispatcher, override);
            return result;
        }, BCMSDispatcher.init(inital));
    }

    // init :: ARRAY -> BCMSDispatcher
    // Return a new BCMSDispatcher
    static init(handlers) {
        return new BCMSDispatcher(handlers);
    }
}